/* eslint-disable @typescript-eslint/naming-convention */
import { format, fromUnixTime } from '@ps/web-poker-dates';
import { utcToZonedTime } from 'date-fns-tz';
import type { MessageDescriptor } from 'react-intl';

import { AttributeKey } from '../formatters/types';
import type { Language, PokerGameTileAttribute } from '../types';

import { Locales, isRegOpen, isRunning } from './mtt.types';

enum TimesInSeconds {
  M = 60,
  H = 60 * 60,
  D = 24 * H
}

export interface FormatTimeOptions {
  seconds: number;
  startTimestamp?: number;
  language: Language;
}

export interface FormatTimeDisplayOptions {
  nowTimestamp?: number;
  startTimestamp?: number;
  lateRegTimestamp?: number;
  i18nInstance: (id: MessageDescriptor['id']) => string;
  language: Language;
}

const secondsToHMS = (seconds: number) => {
  const hrs = Math.floor(seconds / TimesInSeconds.H);
  const mins = Math.floor((seconds % TimesInSeconds.H) / TimesInSeconds.M);
  const secs = Math.floor(seconds % 60);

  return {
    hrs,
    mins,
    secs
  };
};

export const formatTime = (
  date: number | Date,
  dateFormat: string,
  language: Language = 'en'
): string => {
  if (typeof date === 'object') {
    return format(date, dateFormat, { locale: Locales[language] });
  }
  const { hrs, mins, secs } = secondsToHMS(date);
  const formats: Record<string, string> = {
    'h m': `${hrs}h ${mins}min`,
    m: `${mins}min`,
    'm s': `${mins}min ${secs}sec`,
    s: `${secs}sec`
  };

  return formats[dateFormat];
};

const isInRange = (value: number, [min, max]: number[]) => value >= min && value < max;

export const formatTimeAttribute = (options: FormatTimeOptions) => {
  const { seconds, startTimestamp = 0, language } = options;

  if (seconds < TimesInSeconds.M) return formatTime(seconds, 's', language);
  if (isInRange(seconds, [TimesInSeconds.M, 5 * TimesInSeconds.M])) {
    return formatTime(seconds, 'm s', language);
  }
  if (isInRange(seconds, [5 * TimesInSeconds.M, TimesInSeconds.H])) {
    return formatTime(seconds, 'm', language);
  }
  if (isInRange(seconds, [TimesInSeconds.H, 2 * TimesInSeconds.H])) {
    return formatTime(seconds, 'h m', language);
  }
  if (isInRange(seconds, [2 * TimesInSeconds.H, 12 * TimesInSeconds.H])) {
    return formatTime(fromUnixTime(startTimestamp), 'HH:mm', language);
  }
  if (isInRange(seconds, [12 * TimesInSeconds.H, 6 * TimesInSeconds.D])) {
    return formatTime(fromUnixTime(startTimestamp), 'EEE, HH:mm', language);
  }

  return formatTime(fromUnixTime(startTimestamp), 'LLL do, HH:mm', language);
};

const formatStartTimeDisplay = ({
  nowTimestamp,
  startTimestamp,
  i18nInstance,
  language
}: FormatTimeDisplayOptions): PokerGameTileAttribute => {
  let name = i18nInstance('TXTWBCLI_Starts_In');
  const difference = startTimestamp && nowTimestamp ? startTimestamp - nowTimestamp : 0;
  const isStartTime = difference >= 2 * TimesInSeconds.H;

  if (isStartTime) name = i18nInstance('TXTWBCLI_Start_Time');
  return {
    name,
    value: formatTimeAttribute({ seconds: difference, startTimestamp, language }),
    key: isStartTime ? AttributeKey.startTime : AttributeKey.startsIn
  };
};

const formatRunningTimeDisplay = ({
  nowTimestamp,
  startTimestamp,
  i18nInstance,
  language
}: FormatTimeDisplayOptions): PokerGameTileAttribute | undefined => {
  if (nowTimestamp && startTimestamp) {
    const difference = nowTimestamp - startTimestamp;
    if (difference >= 2 * TimesInSeconds.H) return undefined;

    return {
      name: i18nInstance('TXTWBCLI_Running'),
      value: formatTimeAttribute({ seconds: difference, language }),
      key: AttributeKey.running
    };
  }
};

const formatLateRegTimeDisplay = ({
  nowTimestamp,
  lateRegTimestamp,
  i18nInstance,
  language
}: FormatTimeDisplayOptions): PokerGameTileAttribute | undefined => {
  if (lateRegTimestamp && nowTimestamp) {
    const difference = lateRegTimestamp - nowTimestamp;
    if (difference >= 2 * TimesInSeconds.H) return undefined;

    return {
      name: i18nInstance('TXTWBCLI_Late_RegX'),
      value: formatTimeAttribute({ seconds: difference, language }),
      key: AttributeKey.lateReg
    };
  }
};

export const getMttStartTime = ({
  timeZone,
  startTimestamp,
  lateRegTimestamp,
  regStartTimestamp
}: {
  timeZone: string | undefined;
  startTimestamp: number;
  lateRegTimestamp: number;
  regStartTimestamp?: number;
}) => {
  const now = Date.now();
  const startTs = timeZone
    ? +utcToZonedTime(startTimestamp * 1000, timeZone) / 1000
    : startTimestamp;
  const regOpensTs =
    (timeZone && regStartTimestamp
      ? +utcToZonedTime(regStartTimestamp * 1000, timeZone) / 1000
      : regStartTimestamp) || 0;
  const lateRegTs =
    timeZone && lateRegTimestamp
      ? +utcToZonedTime(lateRegTimestamp * 1000, timeZone) / 1000
      : lateRegTimestamp;
  const nowTimestamp = timeZone
    ? Math.round(+utcToZonedTime(now, timeZone) / 1000)
    : Math.round(now / 1000);
  const isLateRegPeriod = lateRegTs && startTs < nowTimestamp && nowTimestamp < lateRegTs;
  const startsInFuture = startTs > nowTimestamp;

  return { startTs, lateRegTs, nowTimestamp, isLateRegPeriod, startsInFuture, regOpensTs };
};

export const formatTimeDisplay = (
  startTimestamp: number,
  lateRegTimestamp: number,
  timeZone: string | undefined,
  i18nInstance: (id: MessageDescriptor['id']) => string,
  fakeStatus: number,
  language: Language
): PokerGameTileAttribute | undefined => {
  if (!startTimestamp && !lateRegTimestamp) return undefined;

  const { isLateRegPeriod, nowTimestamp, lateRegTs, startTs, startsInFuture } = getMttStartTime({
    lateRegTimestamp,
    startTimestamp,
    timeZone
  });

  if (startsInFuture) {
    return formatStartTimeDisplay({
      nowTimestamp,
      startTimestamp: startTs,
      i18nInstance,
      language
    });
  }

  if (isRunning(fakeStatus) && (!isLateRegPeriod || (isLateRegPeriod && !isRegOpen(fakeStatus)))) {
    return formatRunningTimeDisplay({
      nowTimestamp,
      startTimestamp: startTs,
      i18nInstance,
      language
    });
  }

  if (isLateRegPeriod && isRegOpen(fakeStatus)) {
    return formatLateRegTimeDisplay({
      nowTimestamp,
      lateRegTimestamp: lateRegTs,
      i18nInstance,
      language
    });
  }

  return undefined;
};
